import React, { useState, useMemo, useEffect, useCallback } from 'react';
import Sidebar from '../Components/Sidebar.tsx';
import axios from "axios";
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { FaFileExcel } from 'react-icons/fa';

type Customer = {
  Client_ID: string;
  Branch_ID: string;
  CustID: number;
  FullName: string;
  Address: string;
  City: string;
  ContactNo: string;
  InsertedDate: string;
  Email: string | null;
  Profession: string | null;
  ExpectedDate: string | null;
  UpdatedDate: string | null;
  TinNo: string | null;
  IsActive: number | boolean;
  Anniversary: string | null;
  DOB: string | null;
  InsertedBy: string | null;
  Contact2: string | null;
  State: string | null;
  UID: string | null;
  PinCode: string | null;
  AcNo: string | null;
  BankName: string | null;
  IFCSCode: string | null;
  AcName: string | null;
  LoginCount: number | null;
  IsLogin: number | boolean;
  LastLoginDate: string | null;
  ProfilePicture: string | null;
  UPID: string | null;
  UPINumber: string | null;
  Role: string | null;
  GSTIN: string | null;
  Branch: string | null;
  Status: string;
  KYC: string;
};

type InfluencerApiResponse = {
  success: boolean;
  data: Customer[];
};

const FILTER_TABS = [
  { key: 'all', label: 'All' },
  { key: 'pending', label: 'Pending' },
  { key: 'approved', label: 'Approved' },
  { key: 'reject', label: 'Rejected' },
  { key: 'suspect', label: 'Suspect' },
  { key: 'kyc', label: 'KYC Approved' },
] as const;

const STATUS_OPTIONS = [
  { value: '', label: 'Select Status' },
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'reject', label: 'Rejected' },
  { value: 'suspect', label: 'Suspect' },
];

const VISIBLE_COLUMNS = [
  { key: 'FullName', label: 'Full Name', type: 'text', minWidth: 160 },
  { key: 'ContactNo', label: 'Mobile', type: 'text' },
  { key: 'State', label: 'State', type: 'select' },
  { key: 'City', label: 'City', type: 'text' },
  { key: 'Profession', label: 'Profession', type: 'text' },
  { key: 'KYC', label: 'KYC', type: 'kyc' },
] as const;

const formatDate = (dateStr: string | null): string => {
  if (!dateStr) return '--';
  const date = new Date(dateStr);
  return date.toISOString().split('T')[0];
};

const InfluenceersScreen: React.FC = () => {
  const [activeTab, setActiveTab] = useState<(typeof FILTER_TABS)[number]['key']>('all');
  const [searchFilters, setSearchFilters] = useState<Record<string, string>>({});
  const [tableData, setTableData] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const exportToExcel = () => {
    const dataForExport = tableData.map(row => ({
      'Client ID': row.Client_ID,
      'Branch ID': row.Branch_ID,
      'Cust ID': row.CustID,
      'Full Name': row.FullName,
      'Mobile': row.ContactNo,
      'State': row.State,
      'City': row.City,
      'Profession': row.Profession,
      'Created': formatDate(row.InsertedDate),
      'Status': row.Status,
      'KYC': row.KYC === 'on' ? 'Approved' : 'Not Approved',
    }));

    const worksheet = XLSX.utils.json_to_sheet(dataForExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Influencers");
    XLSX.writeFile(workbook, `Influencers_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await axios.get<InfluencerApiResponse>("http://kkk.biznweb.in:5000/api/influencers");
        const raw = response.data.data ?? [];
        const normalized = raw.map((item) => {
          let status: 'pending' | 'approved' | 'reject' | 'suspect' | null = null;
          const apiStatus = (item.Status || '').toString().trim();
          if (apiStatus) {
            const lower = apiStatus.toLowerCase();
            if (lower === 'approved') status = 'approved';
            else if (lower === 'rejected' || lower === 'reject') status = 'reject';
            else if (lower === 'suspect') status = 'suspect';
            else if (lower === 'pending') status = 'pending';
          }
          return {
            ...item,
            FullName: item.FullName ?? "",
            ContactNo: item.ContactNo ?? "",
            State: item.State ?? "",
            City: item.City ?? "",
            InsertedDate: item.InsertedDate ?? "",
            InsertedBy: item.InsertedBy ?? "--",
            Profession: item.Profession ?? "--",
            KYC: item.KYC ?? "off",
            status,
          };
        });
        setTableData(normalized);
      } catch (err) {
        console.error(err);
        setError("Failed to load data");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const fetchInfluencers = useCallback(async () => {
    const res = await axios.get("http://kkk.biznweb.in:5000/api/influencers");
    setTableData(res.data.data);
  }, []);

  const updateInfluencerStatus = useCallback(async (custID: number, newStatus: 'pending' | 'approved' | 'reject' | 'suspect') => {
    setTableData(prev =>
      prev.map(inf =>
        inf.CustID === custID ? { ...inf, Status: newStatus } : inf
      )
    );

    try {
      await axios.put(`http://kkk.biznweb.in:5000/api/influencers/${custID}/status`, { status: newStatus });
      fetchInfluencers();
    } catch (err) {
      console.error("Failed to update status:", err);
      alert("Failed to update status. Please try again.");
      setTableData(prev => prev.map(inf =>
        inf.CustID === custID ? { ...inf, Status: inf.Status } : inf
      ));
    }
  }, [fetchInfluencers]);

  const toggleKycStatus = useCallback(async (custID: number, currentKyc: string) => {
    const newKyc = currentKyc === 'on' ? 'off' : 'on';
    
    setTableData(prev =>
      prev.map(inf =>
        inf.CustID === custID ? { ...inf, KYC: newKyc } : inf
      )
    );

    try {
      await axios.put(`http://kkk.biznweb.in:5000/api/influencers/${custID}/kyc`, { kyc: newKyc });
    } catch (err) {
      console.error("Failed to update KYC:", err);
      alert("Failed to update KYC status.");
      setTableData(prev =>
        prev.map(inf =>
          inf.CustID === custID ? { ...inf, KYC: currentKyc } : inf
        )
      );
    }
  }, []);

  const handleSearchChange = useCallback((key: string, value: string) => {
    setSearchFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const allStates = useMemo(() => {
    return [...new Set(tableData.map((d) => d.State).filter(Boolean))];
  }, [tableData]);

  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = { all: tableData.length, kyc: 0 };
    tableData.forEach(row => {
      counts[row.Status] = (counts[row.Status] || 0) + 1;
      if (row.KYC === 'on') counts.kyc += 1;
    });
    return counts;
  }, [tableData]);

  const filteredData = useMemo(() => {
    return tableData.filter((row) => {
      if (activeTab === 'kyc') {
        return row.KYC === 'on';
      }
      if (activeTab !== 'all' && row.Status !== activeTab) return false;
      
      return VISIBLE_COLUMNS.every((col) => {
        const filterValue = searchFilters[col.key]?.toLowerCase().trim();
        if (!filterValue) return true;

        if (col.key === 'KYC') {
          if (filterValue === 'on' && row.KYC !== 'on') return false;
          if (filterValue === 'off' && row.KYC !== 'off') return false;
          return true;
        }

        const cellValue = row[col.key as keyof Customer];
        const strValue = (cellValue ?? '').toString().toLowerCase();
        return strValue.includes(filterValue);
      });
    });
  }, [tableData, searchFilters, activeTab]);

  const filteredWithSrNo = useMemo(() => {
    return filteredData.map((row, index) => ({ ...row, srNo: index + 1 }));
  }, [filteredData]);

  const handleSrNoFilter = (value: string) => {
    if (!value) {
      setSearchFilters(prev => {
        const { srNo, ...rest } = prev;
        return rest;
      });
      return;
    }

    const [start, end] = value.split('-').map(Number);
    if (isNaN(start) || isNaN(end) || start > end) return;

    setSearchFilters(prev => ({ ...prev, srNo: value }));
  };

  const srNoFilteredData = useMemo(() => {
    const srNoFilter = searchFilters.srNo;
    if (!srNoFilter) return filteredWithSrNo;

    const [start, end] = srNoFilter.split('-').map(Number);
    if (isNaN(start) || isNaN(end)) return filteredWithSrNo;

    return filteredWithSrNo.filter(row => row.srNo >= start && row.srNo <= end);
  }, [filteredWithSrNo, searchFilters.srNo]);

  const handleTabChange = (key: typeof activeTab) => {
    setActiveTab(key);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return '#3b82f6';
      case 'approved': return '#10b981';
      case 'reject': return '#ef4444';
      case 'suspect': return '#f59e0b';
      default: return '#94a3b8';
    }
  };

  const renderKycCell = (kycStatus: string, custID: number) => {
    const isApproved = kycStatus === 'on';
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span style={{
          padding: '4px 10px',
          borderRadius: '20px',
          fontSize: '12px',
          fontWeight: 600,
          color: isApproved ? '#065f46' : '#b91c1c',
          backgroundColor: isApproved ? '#dcfce7' : '#fee2e2',
        }}>
          {isApproved ? 'Approved' : 'Not Approved'}
        </span>
        <button
          onClick={() => toggleKycStatus(custID, kycStatus)}
          style={{
            padding: '4px 10px',
            background: isApproved ? '#ef4444' : '#10b981',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            fontSize: '12px',
            cursor: 'pointer',
            fontWeight: 500,
          }}
        >
          {isApproved ? 'Revoke' : 'Approve'}
        </button>
      </div>
    );
  };

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f8fafc' }}>
      <Sidebar />
      <div style={{ flex: 1, padding: '24px' }}>
        {/* === Fixed Header Section (Never Scrolls) === */}
        <div style={{ marginLeft: '280px', marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
          <h1 style={{ margin: 0, fontSize: 26, fontWeight: 700, color: '#0f172a', letterSpacing: '-0.5px' }}>
            Influencers
          </h1>
          <button
            onClick={exportToExcel}
            disabled={loading || tableData.length === 0}
            style={{
              padding: '10px 18px',
              background: loading || tableData.length === 0 ? '#cbd5e1' : '#10b981',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              fontWeight: 600,
              fontSize: '14px',
              cursor: loading || tableData.length === 0 ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              boxShadow: '0 2px 6px rgba(16, 185, 129, 0.3)',
              transition: 'background 0.2s, transform 0.2s',
            }}
            onMouseEnter={(e) => {
              if (!loading && tableData.length > 0) {
                e.currentTarget.style.transform = 'scale(1.03)';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
            }}
          >
            <FaFileExcel size={18} />
            Export to Excel
          </button>
        </div>

        <div style={{ marginLeft: '280px', display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center', marginBottom: 16 }}>
          {FILTER_TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => handleTabChange(tab.key)}
              style={{
                padding: '10px 20px',
                borderRadius: '12px',
                border: 'none',
                cursor: 'pointer',
                backgroundColor: activeTab === tab.key ? '#1e40af' : '#e2e8f0',
                color: activeTab === tab.key ? '#fff' : '#334155',
                fontWeight: 600,
                fontSize: 15,
                transition: 'all 0.25s ease',
                boxShadow: activeTab === tab.key 
                  ? '0 4px 8px rgba(30, 64, 175, 0.2)' 
                  : '0 2px 4px rgba(0,0,0,0.05)',
                transform: activeTab === tab.key ? 'translateY(-2px)' : 'translateY(0)',
              }}
            >
              {tab.label} {statusCounts[tab.key] !== undefined ? (
                <span style={{
                  background: activeTab === tab.key ? 'rgba(255,255,255,0.2)' : '#cbd5e1',
                  color: activeTab === tab.key ? '#fff' : '#475569',
                  borderRadius: '10px',
                  padding: '0 8px',
                  marginLeft: '8px',
                  fontSize: '12px',
                  fontWeight: 600
                }}>
                  {statusCounts[tab.key]}
                </span>
              ) : ''}
            </button>
          ))}
        </div>

        {/* Fixed Filter Inputs Row */}
        {/* === Modern Filter Row (Matches Screenshot) === */}
{!loading && !error && (
  <div style={{
    marginLeft: '280px',
    background: '#fff',
    borderRadius: '12px',
    padding: '16px',
    marginBottom: 16,
    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
    border: '1px solid #e2e8f0',
    display: 'flex',
    gap: '12px',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'start'
  }}>
    {/* Sr. No. Filter */}
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      gap: '4px',
      minWidth: 120,
      flex: '1 1 auto'
    }}>
      <label style={{
        fontSize: '13px',
        fontWeight: 500,
        color: '#475569',
        marginBottom: '4px'
      }}>
        Sr. No.
      </label>
      <select
        value={searchFilters.srNo || ''}
        onChange={(e) => handleSrNoFilter(e.target.value)}
        style={{
          padding: '6px 10px',
          fontSize: '14px',
          borderRadius: '8px',
          border: '1px solid #cbd5e1',
          background: '#f8fafc',
          width: '50%',
          outline: 'none',
          transition: 'border-color 0.2s',
          cursor: 'pointer'
        }}
        onMouseEnter={(e) => e.currentTarget.style.borderColor = '#94a3b8'}
        onMouseLeave={(e) => e.currentTarget.style.borderColor = '#cbd5e1'}
      >
        <option value="">All</option>
        <option value="1-10">1 - 10</option>
        <option value="1-20">1 - 20</option>
        <option value="1-30">1 - 30</option>
        <option value="1-50">1 - 50</option>
        <option value="1-100">1 - 100</option>
      </select>
    </div>

    {VISIBLE_COLUMNS.map((col) => (
      <div key={col.key} style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        minWidth: col.minWidth ?? 120,
        flex: '1 1 auto'
      }}>
        <label style={{
          fontSize: '13px',
          fontWeight: 500,
          color: '#475569',
          marginBottom: '4px'
        }}>
          {col.label}
        </label>
        {col.type === 'select' ? (
          <select
            value={searchFilters[col.key] || ''}
            onChange={(e) => handleSearchChange(col.key, e.target.value)}
            style={{
              padding: '6px 10px',
              fontSize: '14px',
              borderRadius: '8px',
              border: '1px solid #cbd5e1',
              background: '#f8fafc',
              width: '90%',
              height: '30px',
              outline: 'none',
              transition: 'border-color 0.2s',
              cursor: 'pointer'
            }}
            onMouseEnter={(e) => e.currentTarget.style.borderColor = '#94a3b8'}
            onMouseLeave={(e) => e.currentTarget.style.borderColor = '#cbd5e1'}
          >
            <option value="">All</option>
            {allStates.map((state) => (
              <option key={state} value={state}>
                {state}
              </option>
            ))}
          </select>
        ) : col.type === 'kyc' ? (
          <select
            value={searchFilters[col.key] || ''}
            onChange={(e) => handleSearchChange(col.key, e.target.value)}
            style={{
              padding: '6px 10px',
              fontSize: '12px',
              borderRadius: '8px',
              border: '1px solid #cbd5e1',
              background: '#f8fafc',
              width: '90%',
              outline: 'none',
              height: '30px',
              transition: 'border-color 0.2s',
              cursor: 'pointer'
            }}
            onMouseEnter={(e) => e.currentTarget.style.borderColor = '#94a3b8'}
            onMouseLeave={(e) => e.currentTarget.style.borderColor = '#cbd5e1'}
          >
            <option value="">All</option>
            <option value="on">Approved</option>
            <option value="off">Not Approved</option>
          </select>
        ) : (
          <input
            type="text"
            placeholder={`Search ${col.label}`}
            value={searchFilters[col.key] || ''}
            onChange={(e) => handleSearchChange(col.key, e.target.value)}
            style={{
              padding: '6px 10px',
              fontSize: '14px',
              borderRadius: '8px',
              border: '1px solid #cbd5e1',
              background: '#f8fafc',
              width: '80%',
              outline: 'none',
              transition: 'border-color 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.borderColor = '#94a3b8'}
            onMouseLeave={(e) => e.currentTarget.style.borderColor = '#cbd5e1'}
          />
        )}
      </div>
    ))}
  </div>
)}

        {loading && (
          <div style={{ textAlign: 'center', padding: 40, color: '#1e40af', marginLeft: '280px' }}>
            <div style={{ fontSize: '18px', fontWeight: 500 }}>Loading influencers...</div>
          </div>
        )}

        {error && (
          <div
            style={{
              padding: 18,
              background: '#fee2e2',
              color: '#b91c1c',
              borderRadius: 12,
              marginBottom: 24,
              marginLeft: '280px',
              border: '1px solid #fecaca',
            }}
          >
            {error}
          </div>
        )}

        {/* === Scrollable Table Container === */}
        {!loading && !error && (
          <div
           style={{
            marginLeft: '280px',
            background: '#fff',
            borderRadius: 16,
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            minHeight: '300px',                    // ← Minimum visible area
            height: srNoFilteredData.length === 0 ? 'auto' : 'calc(100vh - 340px)',
            maxHeight: 'calc(150vh - 240px)',     // ← Cap max height
            overflowY: srNoFilteredData.length === 0 ? 'hidden' : 'auto',
            animation: 'fadeIn 0.4s ease',
          }}
          >
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14, minWidth: 800 }}>
                <thead>
                  <tr>
                    <th style={{ ...styles.th, width: '60px' }}>Sr. No.</th>
                    {VISIBLE_COLUMNS.map((col) => (
                      <th
                        key={col.key}
                        style={{
                          ...styles.th,
                          minWidth: col.minWidth ?? 120,
                          padding: '14px 12px',
                          fontSize: '14px',
                          color: '#334155',
                        }}
                      >
                        {col.label}
                      </th>
                    ))}
                    <th style={{ ...styles.th, padding: '14px 12px', fontSize: '14px', color: '#1e293b' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {srNoFilteredData.length === 0 ? (
                    <tr>
                      <td
                        colSpan={VISIBLE_COLUMNS.length + 2}
                        style={{
                          textAlign: 'center',
                          background: '#f7fafc',
                          height: '500px',
                          marginLeft: '280px',
                        }}
                      >
                        <div style={{ 
                          display: 'flex', 
                          flexDirection: 'column', 
                          alignItems: 'center', 
                          gap: '40px',
                          maxWidth: '500px',
                          margin: '0 auto'
                        }}>
                          <div style={{ 
                            width: '160px', 
                            height: '160px', 
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            animation: 'float 3s ease-in-out infinite'
                          }}>
                            <svg viewBox="0 0 200 200" style={{ width: '100%', height: '100%' }}>
                              <circle cx="100" cy="90" r="30" fill="#E0C9A6" />
                              <ellipse cx="75" cy="70" rx="10" ry="20" fill="#D4B89A" transform="rotate(-20 75 70)" />
                              <ellipse cx="125" cy="70" rx="10" ry="20" fill="#D4B89A" transform="rotate(20 125 70)" />
                              <circle cx="90" cy="85" r="4" fill="#333" />
                              <circle cx="110" cy="85" r="4" fill="#333" />
                              <circle cx="100" cy="105" r="6" fill="#D4B89A" />
                              <circle cx="100" cy="105" r="2" fill="#333" />
                              <path d="M70 60 Q60 40 65 30 Q75 35 78 45" fill="#C8A88A" stroke="#B39477" strokeWidth="2" />
                              <path d="M130 60 Q140 40 135 30 Q125 35 122 45" fill="#C8A88A" stroke="#B39477" strokeWidth="2" />
                              <path d="M30 150 Q20 140 25 130 Q35 135 40 140" fill="white" stroke="#ccc" strokeWidth="2" />
                              <circle cx="30" cy="150" r="8" fill="white" stroke="#ccc" strokeWidth="2" />
                            </svg>
                          </div>
                          <div>
                            <h3 style={{ 
                              margin: '0 0 8px', 
                              fontSize: '20px', 
                              fontWeight: 700, 
                              color: '#1e293b' 
                            }}>
                              Oops! No influencers here 🐐
                            </h3>
                            <p style={{ 
                              margin: 0, 
                              color: '#64748b', 
                              fontSize: '15px',
                              lineHeight: 1.5,
                            }}>
                              {activeTab === 'all' 
                                ? 'No influencers found. Try adding some data!' 
                                : activeTab === 'kyc'
                                ? 'No KYC approved influencers yet.'
                                : `No ${FILTER_TABS.find(t => t.key === activeTab)?.label.toLowerCase()} influencers yet.`}
                            </p>
                          </div>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    srNoFilteredData.map((row) => (
                      <tr 
                        key={row.CustID} 
                        style={{
                          ...styles.tr,
                          transition: 'background 0.2s, transform 0.25s ease, box-shadow 0.2s',
                          transformOrigin: 'center',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = '#f1f5f9';
                          e.currentTarget.style.transform = 'scale(1.01)';
                          e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.06)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'transparent';
                          e.currentTarget.style.transform = 'scale(1)';
                          e.currentTarget.style.boxShadow = 'none';
                        }}
                      >
                        <td style={{ ...styles.td, fontWeight: 600, color: '#475569' }}>{row.srNo}</td>
                        {VISIBLE_COLUMNS.map((col) => (
                          <td key={col.key} style={styles.td}>
                            {col.key === 'KYC' ? (
                              renderKycCell(row.KYC, row.CustID)
                            ) : (row[col.key as keyof Customer]?.toString() ?? '--')}
                          </td>
                        ))}
                        <td style={{ ...styles.td, display: 'flex', gap: 10, alignItems: 'center', padding: '14px 12px' }}>
                          <button
                            onClick={() => navigate(`/influencer-detail/${row.CustID}`)}
                            style={{
                              ...styles.actionButton,
                              backgroundColor: '#1e40af',
                              padding: '8px 16px',
                              borderRadius: '8px',
                              fontSize: '14px',
                            }}
                          >
                            View
                          </button>
                          
                          <select
                            value={row.Status}
                            onChange={(e) => updateInfluencerStatus(row.CustID, e.target.value as any)}
                            style={{
                              fontSize: '13px',
                              padding: '8px 12px',
                              borderRadius: '8px',
                              border: '1px solid #e2e8f0',
                              backgroundColor: '#fff',
                              cursor: 'pointer',
                              fontWeight: 500,
                              color: getStatusColor(row.Status),
                              boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                              transition: 'box-shadow 0.2s',
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.1)'}
                            onMouseLeave={(e) => e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.05)'}
                          >
                            {STATUS_OPTIONS.map((opt) => (
                              <option 
                                key={opt.value} 
                                value={opt.value}
                                style={{ color: opt.value ? getStatusColor(opt.value) : '#64748b' }}
                              >
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-8px); }
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @media (max-width: 768px) {
          div[style*="marginLeft: '280px'"] {
            margin-left: 0 !important;
          }
        }
      `}</style>
    </div>
  );
};

const styles = {
  th: {
    padding: '14px 12px',
    textAlign: 'left' as const,
    background: '#f8fafc',
    fontWeight: 600,
    color: '#334155',
    borderBottom: '2px solid #e2e8f0',
    position: 'sticky' as const,
    top: 0,
    zIndex: 2,
  },
  td: {
    padding: '14px 12px',
    borderBottom: '1px solid #eaeaea',
    color: '#334155',
    fontSize: '14px',
  },
  tr: {
    transition: 'background-color 0.2s',
  },
  actionButton: {
    padding: '6px 14px',
    backgroundColor: '#1976d2',
    color: '#fff',
    border: 'none',
    borderRadius: 6,
    cursor: 'pointer',
    fontWeight: 500,
    fontSize: 13,
    transition: 'background 0.2s, transform 0.2s',
  },
};

export default InfluenceersScreen;